import { axiosInstance } from '@/shared/lib/http/instance';
import { signLogBody } from '@/shared/lib/http/request-ticket';
import { encode as base64Encode } from '@/shared/utils/base64';
import { isBoolean, isNumber, isObject, isString } from '@/shared/utils/is';

import type * as IdentityStoreModule from '@/platform/identity/store';

// useUserStore is loaded lazily to break the cycle frontend-alert ↔
// identity/store ↔ identity/api ↔ shared/lib/http/endpoint ↔ axios ↔
// frontend-alert. Static import would resolve axios while it's still
// initializing, producing "endpoint is not a function".
type UserStoreModule = typeof IdentityStoreModule;
let userStoreLoader: Promise<UserStoreModule> | null = null;
let cachedUserStoreModule: UserStoreModule | null = null;
function preloadUserStore(): void {
  if (userStoreLoader || cachedUserStoreModule) return;
  userStoreLoader = import('@/platform/identity/store').then((mod) => {
    cachedUserStoreModule = mod;
    return mod;
  });
}

// Frontend telemetry contract. Numeric constants, sessionStorage key, /log
// endpoint, fingerprint format, and markdown layout are part of the backend
// ingest pipeline contract.

// Benign messages dropped before reporting — shared by handleError (window
// onerror) + captureConsoleError. Native console.error still prints them; this
// only affects what's sent to the backend.
const IGNORED_ERROR_PATTERNS = [
  /^\[Vue warn\]/i,
  /^\[Vue tip\]/i,
  /ResizeObserver loop/i,
  /Download the Vue Devtools/i,
];

const MAX_QUEUE_SIZE = 100;
const REPORT_INTERVAL_MS = 30_000;
const FINGERPRINT_CLEANUP_INTERVAL_MS = 3_600_000; // 60 minutes
const MAX_FINGERPRINT_AGE_MS = 6_000_000; // 100 minutes
const MAX_SAME_ERROR_REPORTS = 10;
const API_ENDPOINT = '/log';
const SESSION_STORAGE_KEY = 'frontend_alert_session_id';
const DUPLICATE_REPORT_THRESHOLDS = [5, 10, 25, 50, 100, 250, 500, 1000, 2500, 5000];
const DUPLICATE_REPORT_GAP_MS = 300_000; // 5 minutes

interface BaseErrorInfo {
  type: string;
  message: string;
  timestamp: number;
  url: string;
  userAgent: string;
  // Optional fields populated by specific paths (window error / resource /
  // unhandled rejection / manual).
  filename?: string;
  lineno?: number;
  colno?: number;
  stack?: string;
  resourceType?: string;
  resourceUrl?: string;
  extra?: Record<string, unknown>;
  isDuplicate?: boolean;
  duplicateCount?: number;
  firstOccurrence?: number;
  timeSinceFirst?: number;
}

interface EnhancedErrorInfo extends BaseErrorInfo {
  userId: string;
  accountId: string;
  userRole: string;
  environment: string;
  version: string;
  buildTime: string;
  viewport: string;
  screen: string;
  language: string;
  referrer: string;
  title: string;
  sessionId: string;
  errorId: string;
}

interface FingerprintRecord {
  fingerprint: string;
  count: number;
  firstOccurrence: number;
  lastOccurrence: number;
  lastReportTime: number;
  reportCount: number;
  error: EnhancedErrorInfo;
}

interface ReportMetadata {
  reportTime: number;
  errorCount: number;
  sentViaBeacon?: boolean;
  fingerprints: Array<{
    fingerprint: string;
    count: number;
    firstOccurrence: number;
    lastOccurrence: number;
  }>;
}

// Module-level state. Functions over a class — no inheritance, no
// instance-per-tab use case, simpler to reset in tests.
let queue: EnhancedErrorInfo[] = [];
const errorFingerprints = new Map<string, FingerprintRecord>();
let isInitialized = false;
let reportTimer: ReturnType<typeof setInterval> | null = null;
let cleanupTimer: ReturnType<typeof setInterval> | null = null;

function getSessionId(): string {
  let sessionId = sessionStorage.getItem(SESSION_STORAGE_KEY);
  if (!sessionId) {
    sessionId = crypto.randomUUID();
    sessionStorage.setItem(SESSION_STORAGE_KEY, sessionId);
  }
  return sessionId;
}

// Sensitive-field masking. Recursive over objects + arrays.
function isSensitiveKey(key: string): boolean {
  const k = key.toLowerCase();
  return (
    k.includes('password') ||
    k.includes('passwd') ||
    k.includes('secret') ||
    k.includes('token') ||
    k.includes('routingkey') ||
    k.includes('integrationkey') ||
    (k.includes('routing') && k.includes('key')) ||
    (k.includes('integration') && k.includes('key')) ||
    (k.includes('key') && k.includes('private'))
  );
}

// Pre-sanitizer for axios request/response bodies before they enter the
// queue. axios serializes config.data to a JSON string before send, so the
// raw `maskSensitiveFields` walk (which only recurses objects + arrays)
// leaves the string untouched and ships secrets verbatim. Try-parse the
// string then mask; if it's not JSON, fall back to a regex pass for common
// password=... shapes.
export function sanitizeRequestData(data: unknown): unknown {
  if (data === null || data === undefined) return data;
  if (typeof data === 'object') return maskSensitiveFields(data);
  if (typeof data !== 'string') return data;
  try {
    return maskSensitiveFields(JSON.parse(data));
  } catch {
    return data
      .replace(/"password"\s*:\s*"[^"]*"/gi, '"password":"***MASKED***"')
      .replace(/password=([^&\s]*)/gi, 'password=***MASKED***');
  }
}

function maskSensitiveFields(value: unknown): unknown {
  if (value === null || typeof value !== 'object') return value;
  if (Array.isArray(value)) return value.map((v) => maskSensitiveFields(v));
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    if (isSensitiveKey(k)) {
      out[k] = '***MASKED***';
    } else if (v !== null && typeof v === 'object') {
      out[k] = maskSensitiveFields(v);
    } else {
      out[k] = v;
    }
  }
  return out;
}

function generateErrorFingerprint(errorInfo: BaseErrorInfo): string {
  const key = `${errorInfo.type}_${errorInfo.message}_${errorInfo.filename ?? ''}_${
    errorInfo.lineno ?? 0
  }`;
  return base64Encode(key)
    .replace(/[^a-zA-Z0-9]/g, '')
    .substring(0, 32);
}

function readUserContext(): { userId: string; accountId: string; userRole: string } {
  // Defensive read — telemetry boots before pinia is ready on some paths
  // (window error fires during early bootstrap, or store module not yet
  // resolved). Wrap in try/catch.
  preloadUserStore();
  if (!cachedUserStoreModule) {
    return { userId: '', accountId: '', userRole: '' };
  }
  try {
    const userStore = cachedUserStoreModule.useUserStore();
    const info = userStore.info ?? {};
    return {
      userId: (info as { userID?: string }).userID ?? '',
      accountId: (info as { accountID?: string }).accountID ?? '',
      userRole: (info as { role?: string }).role ?? '',
    };
  } catch {
    return { userId: '', accountId: '', userRole: '' };
  }
}

function enhanceErrorData(errorInfo: BaseErrorInfo): EnhancedErrorInfo {
  const ctx = readUserContext();
  return {
    ...errorInfo,
    userId: ctx.userId,
    accountId: ctx.accountId,
    userRole: ctx.userRole,
    environment: import.meta.env.MODE ?? 'unknown',
    // Vite's `import.meta.env` doesn't carry version / buildTime in the v3
    // build today — keep the "unknown" sentinel so the markdown formatter's
    // `&& !== 'unknown'` checks still work.
    version: 'unknown',
    buildTime: 'unknown',
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    screen: `${window.screen.width}x${window.screen.height}`,
    language: navigator.language,
    referrer: document.referrer,
    title: document.title,
    sessionId: getSessionId(),
    errorId: crypto.randomUUID(),
  };
}

function shouldReportDuplicate(existing: FingerprintRecord): boolean {
  const now = Date.now();
  const timeSinceLastReport = now - (existing.lastReportTime || existing.firstOccurrence);
  if (timeSinceLastReport > DUPLICATE_REPORT_GAP_MS) return true;
  if (DUPLICATE_REPORT_THRESHOLDS.includes(existing.count)) {
    // Cap re-emits at maxSameErrorReports — once we've reported this
    // fingerprint that many times, fall silent until cleanup expires it.
    if ((existing.reportCount || 1) >= MAX_SAME_ERROR_REPORTS) return false;
    return true;
  }
  return false;
}

function reportError(errorInfo: BaseErrorInfo): void {
  try {
    const fingerprint = generateErrorFingerprint(errorInfo);
    const now = Date.now();

    if (errorFingerprints.has(fingerprint)) {
      const existing = errorFingerprints.get(fingerprint)!;
      existing.count++;
      existing.lastOccurrence = now;
      if (shouldReportDuplicate(existing)) {
        const enhanced = enhanceErrorData({
          ...errorInfo,
          isDuplicate: true,
          duplicateCount: existing.count,
          firstOccurrence: existing.firstOccurrence,
          timeSinceFirst: now - existing.firstOccurrence,
        });
        existing.lastReportTime = now;
        existing.reportCount = (existing.reportCount || 1) + 1;
        addToQueue(enhanced);
      }
      return;
    }

    const enhanced = enhanceErrorData({
      ...errorInfo,
      isDuplicate: false,
      duplicateCount: 1,
    });
    errorFingerprints.set(fingerprint, {
      fingerprint,
      count: 1,
      firstOccurrence: now,
      lastOccurrence: now,
      lastReportTime: now,
      reportCount: 1,
      error: enhanced,
    });
    addToQueue(enhanced);
  } catch (error) {
    console.warn('[FrontendAlert] Failed to process error:', error);
  }
}

function addToQueue(errorInfo: EnhancedErrorInfo): void {
  queue.push(errorInfo);
  if (queue.length >= MAX_QUEUE_SIZE) {
    void flushQueue();
  }
}

function cleanupOldFingerprints(): void {
  const now = Date.now();
  const toDelete: string[] = [];
  for (const [fp, data] of errorFingerprints.entries()) {
    if (now - data.firstOccurrence > MAX_FINGERPRINT_AGE_MS) toDelete.push(fp);
  }
  for (const fp of toDelete) errorFingerprints.delete(fp);
}

function formatBatchReportAsMarkdown(
  errors: EnhancedErrorInfo[],
  metadata: ReportMetadata,
): string {
  const timestamp = new Date(metadata.reportTime).toISOString();
  const errorTypes = [...new Set(errors.map((e) => e.type))];

  let markdown = `**Frontend Error Batch Report**\n\n`;

  const firstError = errors[0];
  if (firstError) {
    markdown += `**Common Environment Information:**\n`;
    markdown += `**User Agent:** ${firstError.userAgent || 'Unknown'}\n`;
    markdown += `**Viewport:** ${firstError.viewport || 'Unknown'}\n`;
    markdown += `**Screen:** ${firstError.screen || 'Unknown'}\n`;
    markdown += `**Language:** ${firstError.language || 'Unknown'}\n`;
    markdown += `**Environment:** ${firstError.environment || 'Unknown'}\n`;
    if (firstError.version && firstError.version !== 'unknown') {
      markdown += `**App Version:** ${firstError.version}\n`;
    }
    if (firstError.buildTime && firstError.buildTime !== 'unknown') {
      markdown += `**Build Time:** ${firstError.buildTime}\n`;
    }
    markdown += `**Session ID:** ${firstError.sessionId}\n`;
    if (firstError.userId) markdown += `**User ID:** ${firstError.userId}\n`;
    if (firstError.accountId) markdown += `**Account ID:** ${firstError.accountId}\n`;
    markdown += `\n`;
  }

  markdown += `**Report Time:** ${timestamp}\n`;
  markdown += `**Total Errors:** ${metadata.errorCount}\n`;
  markdown += `**Error Types:** ${errorTypes.join(', ')}\n\n`;

  const typeCounts: Record<string, number> = {};
  for (const e of errors) typeCounts[e.type] = (typeCounts[e.type] || 0) + 1;
  markdown += `**Error Summary:**\n\n`;
  for (const [type, count] of Object.entries(typeCounts)) {
    const percentage = ((count / errors.length) * 100).toFixed(1);
    markdown += `**${type.replace(/_/g, ' ').toUpperCase()}:** ${count} (${percentage}%)\n`;
  }
  markdown += `\n`;

  markdown += `**Error Details:**\n\n`;
  const displayErrors = errors.slice(0, 5);
  displayErrors.forEach((error, index) => {
    const errorTime = new Date(error.timestamp).toISOString();
    markdown += `**Error ${index + 1}: ${error.type.replace(/_/g, ' ').toUpperCase()}**\n`;
    markdown += `**Message:** ${error.message}\n`;
    markdown += `**Time:** ${errorTime}\n`;
    markdown += `**URL:** ${error.url}\n`;
    if (error.isDuplicate) markdown += `**Occurrences:** ${error.duplicateCount}\n`;
    if (error.filename) markdown += `**File:** ${error.filename}:${error.lineno || 0}\n`;
    if (error.stack) {
      const firstStackLine = error.stack.split('\n')[0];
      markdown += `**Stack:** ${firstStackLine}\n`;
    }
    markdown += `**Error ID:** ${error.errorId}\n`;
    markdown += `\n`;
  });
  if (errors.length > 5) markdown += `... and ${errors.length - 5} more errors\n\n`;

  if (metadata.fingerprints && metadata.fingerprints.length > 0) {
    markdown += `**Error Patterns:**\n\n`;
    metadata.fingerprints.slice(0, 5).forEach((fp) => {
      const duration = fp.lastOccurrence - fp.firstOccurrence;
      const durationStr = duration > 0 ? ` (${Math.round(duration / 1000)}s duration)` : '';
      markdown += `**${fp.fingerprint}:** ${fp.count} occurrences${durationStr}\n`;
    });
    if (metadata.fingerprints.length > 5)
      markdown += `... and ${metadata.fingerprints.length - 5} more patterns\n`;
    markdown += `\n`;
  }

  // Strip common metadata from per-error payloads + mask sensitive fields
  // before embedding the JSON blob. Funnel `extra` through maskSensitiveFields
  // here so any custom caller is safe by default.
  const filteredErrors = errors.map((error) => {
    /* eslint-disable @typescript-eslint/no-unused-vars */
    const {
      userAgent,
      viewport,
      screen,
      language,
      environment,
      version,
      buildTime,
      sessionId,
      userId,
      accountId,
      extra,
      ...rest
    } = error;
    /* eslint-enable @typescript-eslint/no-unused-vars */
    return {
      ...rest,
      ...(extra !== undefined ? { extra: maskSensitiveFields(extra) } : {}),
    };
  });

  markdown += `**Complete Batch Data:**\n`;
  markdown += `\`\`\`json\n${JSON.stringify(
    { errors: filteredErrors, metadata },
    null,
    2,
  )}\n\`\`\`\n`;
  return markdown;
}

async function sendReport(errors: EnhancedErrorInfo[]): Promise<void> {
  const metadata: ReportMetadata = {
    reportTime: Date.now(),
    errorCount: errors.length,
    fingerprints: Array.from(errorFingerprints.values()).map((fp) => ({
      fingerprint: fp.fingerprint,
      count: fp.count,
      firstOccurrence: fp.firstOccurrence,
      lastOccurrence: fp.lastOccurrence,
    })),
  };
  const markdownReport = formatBatchReportAsMarkdown(errors, metadata);
  await axiosInstance.post(API_ENDPOINT, { message: markdownReport });
}

async function flushQueue(): Promise<void> {
  if (queue.length === 0) return;
  const errors = [...queue];
  queue = [];
  try {
    await sendReport(errors);
  } catch (error) {
    console.warn('[FrontendAlert] Failed to report errors:', error);
    // Failure → re-queue, but cap at MAX_QUEUE_SIZE to avoid runaway.
    if (queue.length < MAX_QUEUE_SIZE) {
      queue.unshift(...errors.slice(0, MAX_QUEUE_SIZE - queue.length));
    }
  }
}

function sendUnloadReport(): void {
  if (typeof fetch !== 'function') {
    void flushQueue();
    return;
  }
  const errors = [...queue];
  queue = [];
  if (errors.length === 0) return;
  const metadata: ReportMetadata = {
    reportTime: Date.now(),
    errorCount: errors.length,
    sentViaBeacon: true,
    fingerprints: Array.from(errorFingerprints.values()).map((fp) => ({
      fingerprint: fp.fingerprint,
      count: fp.count,
      firstOccurrence: fp.firstOccurrence,
      lastOccurrence: fp.lastOccurrence,
    })),
  };
  let markdownReport = formatBatchReportAsMarkdown(errors, metadata);
  markdownReport += `\n\n**Sent via fetch keepalive (page unload)**`;

  const bodyStr = JSON.stringify({ message: markdownReport });
  const base = axiosInstance.defaults.baseURL ?? '';
  const url = `${base}${API_ENDPOINT}`;
  try {
    void fetch(url, {
      method: 'POST',
      keepalive: true,
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...signLogBody(bodyStr),
      },
      body: bodyStr,
    }).catch(() => {
      /* Unload is best-effort; never throw. */
    });
  } catch {
    /* Unload is best-effort; never throw. */
  }
}

// Bound listener references — needed so destroy() / re-init cleanup can
// remove the same function references later if it ever wires up.
function handleError(event: ErrorEvent): void {
  // Resource-load errors also bubble here (capture phase) with the
  // failing element as the target; skip them — handleResourceError owns
  // those (avoids "Unknown error" + duplicate reports). HTMLElement is
  // the right discriminator: window / document are not HTMLElements, and
  // it survives jsdom's separate-proxy window quirk that breaks an
  // identity check.
  if (event.target instanceof HTMLElement) return;
  // Drop benign browser noise (e.g. ResizeObserver loop) arriving via window.onerror.
  if (IGNORED_ERROR_PATTERNS.some((re) => re.test(event.message ?? ''))) return;
  reportError({
    type: 'javascript_error',
    message: event.message || 'Unknown error',
    filename: event.filename || 'Unknown file',
    lineno: event.lineno || 0,
    colno: event.colno || 0,
    stack: event.error?.stack || 'No stack trace',
    timestamp: Date.now(),
    url: window.location.href,
    userAgent: navigator.userAgent,
  });
}

function handleUnhandledRejection(event: Event): void {
  const reason = (event as Event & { reason?: unknown }).reason;
  if (reason && typeof reason === 'object') {
    const r = reason as { manuallyBlocked?: boolean; reportedByInterceptor?: boolean };
    // Deliberate gate (reported as blocked_request by the axios error
    // interceptor) — skip + suppress the console "Uncaught (in promise)"
    // noise.
    if (r.manuallyBlocked) {
      (event as Event & { preventDefault: () => void }).preventDefault?.();
      return;
    }
    // Already reported as network_request_error by the interceptor — skip
    // to avoid double-counting as a promise_rejection.
    if (r.reportedByInterceptor) return;
  }
  // Prefer the Error-shaped message, fall back to the string form, then to
  // a JSON snapshot — never let an object hit String() and produce
  // '[object Object]'.
  let message: string;
  let stack = 'No stack trace';
  if (typeof reason === 'string') {
    message = reason;
  } else if (reason && typeof reason === 'object') {
    const obj = reason as { message?: unknown; stack?: unknown };
    if (typeof obj.message === 'string') message = obj.message;
    else {
      try {
        message = JSON.stringify(reason);
      } catch {
        message = 'Unhandled promise rejection';
      }
    }
    if (typeof obj.stack === 'string') stack = obj.stack;
  } else {
    message = 'Unhandled promise rejection';
  }
  reportError({
    type: 'promise_rejection',
    message,
    stack,
    timestamp: Date.now(),
    url: window.location.href,
    userAgent: navigator.userAgent,
  });
}

function handleResourceError(event: Event): void {
  // Resource errors bubble through `error` events on document with
  // event.target set to the failing tag (img / script / link / …).
  if (event.target === window) return;
  const target = event.target as HTMLElement & { src?: string; href?: string };
  const url = target.src || target.href || '';
  reportError({
    type: 'resource_error',
    message: `Failed to load resource: ${url || 'Unknown resource'}`,
    resourceType: target.tagName?.toLowerCase() || 'unknown',
    resourceUrl: url,
    timestamp: Date.now(),
    url: window.location.href,
    userAgent: navigator.userAgent,
  });
}

function handleBeforeUnload(): void {
  if (queue.length > 0) sendUnloadReport();
}

// Capture console.error for remote troubleshooting (native call kept;
// filtered). Prefer a real Error (message + stack), drop known dev/browser
// noise.
function captureConsoleError(args: unknown[]): void {
  const errArg = args.find((a): a is Error => a instanceof Error);
  const stringify = (a: unknown): string => {
    if (isString(a)) return a;
    if (isNumber(a) || isBoolean(a)) return String(a);
    if (isObject(a)) return JSON.stringify(a) ?? 'null';
    return String(a as { toString: () => string });
  };
  const message = errArg ? errArg.message : args.map(stringify).join(' ');
  if (!message) return;
  if (IGNORED_ERROR_PATTERNS.some((re) => re.test(message))) return;
  reportError({
    type: 'console_error',
    message,
    stack: errArg?.stack || 'No stack trace',
    timestamp: Date.now(),
    url: window.location.href,
    userAgent: navigator.userAgent,
  });
}

let originalConsoleError: typeof console.error | null = null;
function setupConsoleErrorOverride(): void {
  if (originalConsoleError) return;
  originalConsoleError = console.error.bind(console);
  console.error = (...args: unknown[]) => {
    originalConsoleError?.(...args);
    try {
      captureConsoleError(args);
    } catch {
      /* Reporting must never break console.error itself. */
    }
  };
}

// Wired from main.ts into app.config.errorHandler — Vue render / lifecycle
// errors don't reach window.onerror; this gives a real stack + component info.
export function captureVueError(err: unknown, _vm: unknown, info: string): void {
  const e = err as { message?: string; stack?: string } | null;
  reportError({
    type: 'vue_error',
    message: e?.message || String(err) || 'Vue error',
    stack: e?.stack || 'No stack trace',
    extra: { componentInfo: info || '' },
    timestamp: Date.now(),
    url: window.location.href,
    userAgent: navigator.userAgent,
  });
}

export function init(): void {
  if (isInitialized) return;
  preloadUserStore();
  window.addEventListener('error', handleError, true);
  window.addEventListener('unhandledrejection', handleUnhandledRejection, true);
  document.addEventListener('error', handleResourceError, true);
  window.addEventListener('beforeunload', handleBeforeUnload);
  setupConsoleErrorOverride();

  reportTimer = setInterval(() => {
    if (queue.length > 0) void flushQueue();
  }, REPORT_INTERVAL_MS);
  cleanupTimer = setInterval(cleanupOldFingerprints, FINGERPRINT_CLEANUP_INTERVAL_MS);

  isInitialized = true;
}

export function reportManualError(
  type: string,
  message: string,
  extra: Record<string, unknown> = {},
): void {
  reportError({
    type,
    message,
    extra,
    timestamp: Date.now(),
    url: window.location.href,
    userAgent: navigator.userAgent,
  });
}

// Axios interceptors call this for slow requests, API non-success codes, and
// network errors. Same shape as reportManualError; separate helper documents
// the call sites.
export function reportNetworkError(
  type:
    | 'slow_network_request'
    | 'api_response_error'
    | 'network_request_error'
    | 'request_interceptor_error',
  message: string,
  extra: Record<string, unknown> = {},
): void {
  reportManualError(type, message, extra);
}

// Test-only hook. Resets module state so `beforeEach` can isolate cases
// without spinning up a real teardown / re-init listener cycle.
export function __resetForTests(): void {
  if (reportTimer) clearInterval(reportTimer);
  if (cleanupTimer) clearInterval(cleanupTimer);
  reportTimer = null;
  cleanupTimer = null;
  // Remove listeners so init() inside a test re-registers cleanly.
  window.removeEventListener('error', handleError, true);
  window.removeEventListener('unhandledrejection', handleUnhandledRejection, true);
  window.removeEventListener('beforeunload', handleBeforeUnload);
  document.removeEventListener('error', handleResourceError, true);
  if (originalConsoleError) {
    console.error = originalConsoleError;
    originalConsoleError = null;
  }
  queue = [];
  errorFingerprints.clear();
  isInitialized = false;
  userStoreLoader = null;
  cachedUserStoreModule = null;
}

export { DUPLICATE_REPORT_THRESHOLDS, DUPLICATE_REPORT_GAP_MS };
// Test-only fingerprint accessor — pinning the formula keeps the dedup
// contract stable across refactors.
export { generateErrorFingerprint as __generateFingerprintForTests };

// Test-only: await the lazy `import('@/platform/identity/store')` chain
// (api.ts → endpoint.ts) before the file's afterEach lets vitest tear
// the env down. Without this, a reportError fired late in a test
// triggers the import after teardown and surfaces as a non-test
// EnvironmentTeardownError that tanks the file's exit code.
export function __waitForUserStorePreloadForTests(): Promise<unknown> {
  return userStoreLoader ?? Promise.resolve();
}
